import type { ReactNode, VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { IgnoredKind } from '../../../../../../shared/issuesV2';
import BaseIssueItemContent from './BaseIssueItemContent';
import * as IssuesV2Store from '../../../../../redux/issuesV2';
import { fetcherToPlainText, isLinuxFetcher } from '../../../../../../shared/fetchers';
import Locator from '../../../../../../shared/Locator';
import { parseLinuxPackage } from '../../../../../../shared/linux';
import IssueTitle from '../IssueTitle';
import IgnoredIssueBadge from '../IgnoredIssueBadge';
import IssueTicketBadge from '../IssueTicketBadge';
import EpssBadge from '../EpssBadge';
import WithSeparator from '../WithSeparator';
import { Badge, BadgeColors, FeatureFlagRequired } from '../../../../generic';
import FixBadge from '../FixBadge';
import DepthIndicator from '../DepthIndicator';
import ProjectsList from '../ProjectsList';
import FoundDateBadge from '../FoundDateBadge';
import IgnoredKindBadge from '../IgnoredKindBadge';
import Flex from '../../../../generic/Flex';
import { Exploitability } from '../../../../../../shared/vulns/types';
import CVEBadge from '../CVEBadge';
import KnownExploitBadge from '../KnownExploitBadge';
import type { ReduxStore } from '../../../../../redux/store';
import type { LinuxPackageParts } from '../../../../../../shared/linux';
import type {
  DependencyUsage,
  EpssResponse,
  IgnoredKindMetadata,
  JiraTicket,
  RemediationResponse,
  VulnIssue,
} from '../../../../../../shared/issuesV2';
import { ENABLE_REACHABILITY } from '../../../../../../shared/featureFlags/flagNames';
import ReachabilityBadge from '../ReachabilityBadge';
import UnverifiedBadge from '../unverifiedBadge';

type Props = {
  issue: VulnIssue;
  locator: string;
  title?: string;
  dependency: string;
  version: string | null;
  tickets?: JiraTicket[];
  cve?: string;
  exploitability: Exploitability;
  epss?: EpssResponse;
  remediation?: RemediationResponse;
  badge: ReactNode;
  packageManager: string;
  depths: Record<DependencyUsage, number>;
  createdAt: string;
  ignoredAt?: string;
  ignoredKinds?: IgnoredKindMetadata[];
  isReachable?: boolean;
};

/**
 * This component displays the content of the vulnerability issue row.
 */
const VulnerabilityIssueItemContent: VoidFunctionComponent<Props> = ({
  issue,
  locator,
  title,
  dependency,
  version,
  tickets,
  cve,
  exploitability,
  epss,
  remediation,
  packageManager,
  depths,
  createdAt,
  ignoredAt,
  ignoredKinds,
  badge,
  isReachable,
}) => {
  const grouping = useSelector(IssuesV2Store.selectors.getGrouping);
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issueDetailsURL = useSelector((state: ReduxStore) =>
    IssuesV2Store.selectors.getIssueDetailsURL(state, issue.id));

  const isLinuxDep = isLinuxFetcher(packageManager);

  // Only display fix information if remediation guidance exists and only for non-Linux issues
  const shouldShowFixBadge = remediation && !isLinuxDep;

  const rawPackageName = Locator.parse(locator).package || dependency;
  // the output of parseLinuxPackage is guaranteed to have distroVersion because we are
  // checking if the input is a linux dep, which must have name, distro, and version within it
  const osVersion = isLinuxDep ? ` (${(parseLinuxPackage(rawPackageName) as LinuxPackageParts).distroVersion})` : '';

  // The projects to use for the projects list.  The empty list disables it if we aren't in the global scope.
  const projects = scope.type === 'global' ? issue.projects : [];

  // Whether we are rendering in a grouped view or not for choosing what to render.
  const isUngrouped = grouping === 'issue';

  return (
    <BaseIssueItemContent
      headerSlot={
        <>
          {badge}
          <FeatureFlagRequired flagName={ENABLE_REACHABILITY}>
            {isReachable &&
              <ReachabilityBadge />}
          </FeatureFlagRequired>
          {epss && <EpssBadge tooltipId={`epss-badge-${issue.id}`} epss={epss} />}
          {shouldShowFixBadge && (
            <FixBadge version={remediation.partialFix} distance={remediation.partialFixDistance} />
          )}
          {cve && <CVEBadge cve={cve} />}
          {exploitability !== Exploitability.Unknown && <KnownExploitBadge />}
        </>
      }
      contentSlot={
        <Flex direction="column" align="start" gap="8px">
          <div>
            <IssueTitle
              title={title}
              packageManager={packageManager}
              dependency={dependency}
              version={version}
              issueDetailsURL={issueDetailsURL}
            />
          </div>
          <Flex direction="row" align="start" gap="8px">
            {ignoredAt && <IgnoredIssueBadge ignoredAt={ignoredAt} />}
            {tickets && (tickets?.length ?? 0) > 0 && <IssueTicketBadge tickets={tickets} />}
            {packageManager && isUngrouped && (
              <WithSeparator>
                <Badge color={BadgeColors.White}>
                  {fetcherToPlainText(packageManager) || packageManager}
                  {osVersion}
                </Badge>
              </WithSeparator>
            )}
            {depths && isUngrouped && <DepthIndicator depths={depths} />}
            {projects && isUngrouped && projects.length > 0 && (
              <WithSeparator>
                {' '}
                <ProjectsList projects={projects} />{' '}
              </WithSeparator>
            )}
            {createdAt && <FoundDateBadge createdAt={createdAt} />}
            {issue?.cpes?.length > 0 && <WithSeparator position="before"><UnverifiedBadge /></WithSeparator>}
          </Flex>
        </Flex>
      }
      contentRightSlot={
        ignoredKinds &&
        !!ignoredKinds?.length &&
        ignoredKinds.some((ignore) => ignore.ignoreType === IgnoredKind.IssueException) && (
          <IgnoredKindBadge ignoredKinds={ignoredKinds} numProjects={projects.length} />
        )
      }
    />
  );
};

export default VulnerabilityIssueItemContent;
